import React, { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { CALENDLY_URL } from "@/lib/siteData";

const FAQ_ITEMS = [
  {
    q: "Combien coûte un courtier en crédit ou en assurance ?",
    a: "En placement, je ne facture aucun honoraire au client : je suis rémunéré par des commissions versées par les partenaires. En assurance, des frais de courtage de 25 € (1 contrat), 40 € (2 contrats) ou 50 € (3 contrats et plus) s'appliquent. En crédit, les frais de courtage sont fixés dans le mandat et ne sont dus qu'après le déblocage des fonds. Le détail figure dans les CGV.",
  },
  {
    q: "Qu'est-ce que la loi Lemoine sur l'assurance emprunteur ?",
    a: "La loi Lemoine (2022) permet à tout emprunteur de changer d'assurance emprunteur à tout moment, sans frais ni pénalités, à garanties équivalentes. En déléguant votre assurance à un assureur externe plutôt qu'au contrat groupe bancaire, vous pouvez économiser plusieurs milliers d'euros sur la durée du prêt.",
  },
  {
    q: "PER ou assurance-vie : que choisir pour épargner ?",
    a: "Le PER (Plan d'Épargne Retraite) permet une déduction fiscale à l'entrée mais est bloqué jusqu'à la retraite. L'assurance-vie offre une fiscalité avantageuse après 8 ans et reste disponible à tout moment. Les deux sont souvent complémentaires selon votre tranche d'imposition et vos objectifs.",
  },
  {
    q: "Combien puis-je emprunter avec 3 000 € de salaire ?",
    a: "Le taux d'endettement maximum admis en France est de 35 % des revenus. Avec 3 000 € net/mois, la mensualité maximale théorique est donc de 1 050 €. Sur 25 ans à 3,4 %, cela représente environ 215 000 € empruntables, hors assurance et apport. Mon simulateur en haut de page vous donne une estimation personnalisée.",
  },
  {
    q: "MD Courtage est-il un courtier indépendant ?",
    a: "En assurance, oui : j'exerce comme courtier indépendant, sans aucun lien capitalistique ni participation avec un assureur, et je mets en concurrence plusieurs partenaires. En placement, mon conseil est délivré de manière non indépendante au sens de la réglementation (MIF 2), car je suis rémunéré par des commissions versées par les partenaires et non par des honoraires du client.",
  },
  {
    q: "Dans quels départements intervenez-vous ?",
    a: "J'interviens principalement dans les départements 53 (Mayenne) et 35 (Ille-et-Vilaine). Les rendez-vous peuvent se faire en personne, en visio ou par téléphone selon votre préférence.",
  },
  {
    q: "Comment défiscaliser efficacement en 2026 ?",
    a: "Plusieurs dispositifs existent selon votre profil : PER (déduction du revenu imposable), investissement en titres participatifs, dispositif Denormandie pour l'ancien à rénover, Girardin industriel outre-mer, FCPI/FIP, etc. Le choix dépend de votre tranche d'imposition, votre horizon et votre capacité d'investissement. Un diagnostic personnalisé est indispensable.",
  },
  {
    q: "Comment prendre rendez-vous avec vous ?",
    a: "Vous pouvez prendre rendez-vous directement en ligne via Calendly, par téléphone au 06.88.07.38.33 ou par email à contact@mdcourtage.fr. Le premier rendez-vous est gratuit et sans engagement.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32 bg-md-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-32">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-md-secondary/15 text-md-secondary text-[11px] uppercase tracking-[0.2em] font-bold mb-5">
            <HelpCircle className="w-3.5 h-3.5" />
            Questions fréquentes
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-md-primary font-medium leading-tight mb-6">
            Vous vous posez <span className="italic">des questions ?</span>
          </h2>
          <p className="text-md-muted leading-relaxed mb-8">
            Les réponses aux interrogations les plus courantes. Besoin d'un
            conseil personnalisé ? Discutons de votre situation autour d'un
            café — ou en visio.
          </p>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="faq-cta"
            className="inline-flex items-center gap-2 bg-md-primary hover:bg-md-primaryHover text-white px-6 py-3 rounded-full font-semibold transition-all"
          >
            Poser votre question en RDV
          </a>
        </div>

        <div className="lg:col-span-8 space-y-3" data-testid="faq-list">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                className={`bg-white border rounded-2xl transition-all duration-300 ${
                  isOpen ? "border-md-secondary shadow-md" : "border-md-border hover:border-md-secondary/40"
                }`}
              >
                <button
                  type="button"
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-serif text-lg md:text-xl text-md-primary font-medium leading-snug flex-1">
                    {item.q}
                  </span>
                  <span
                    className={`flex-shrink-0 w-9 h-9 rounded-full grid place-items-center transition-all ${
                      isOpen ? "bg-md-secondary text-white rotate-180" : "bg-md-bg text-md-primary"
                    }`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-300 ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 md:px-6 pb-6 text-md-muted leading-relaxed text-[15px]">
                      {item.a}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
